import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CalendarDays, Download, Images, Inbox, LogOut, Mail, Mic2, Package, ShoppingBag, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { LOGO_URL } from "@/data";
import ConcertsAdmin from "@/pages/admin/ConcertsAdmin";
import BookingsAdmin from "@/pages/admin/BookingsAdmin";
import GalleryAdmin from "@/pages/admin/GalleryAdmin";
import MembersAdmin from "@/pages/admin/MembersAdmin";
import ShopAdmin from "@/pages/admin/ShopAdmin";
import OrdersAdmin from "@/pages/admin/OrdersAdmin";

const TABS = [
  { id: "bookings", label: "Richieste live", Icon: Inbox },
  { id: "orders", label: "Ordini", Icon: Package },
  { id: "shop", label: "Merch", Icon: ShoppingBag },
  { id: "concerts", label: "Date", Icon: CalendarDays },
  { id: "gallery", label: "Foto", Icon: Images },
  { id: "members", label: "Band", Icon: Mic2 },
  { id: "subscribers", label: "Iscritti", Icon: Users },
];

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "lc_admin_token";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Qualcosa è andato storto. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString("it-IT", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
      onLogin(data.token, data.user);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      data-testid="admin-login-form"
      onSubmit={submit}
      className="w-full max-w-md border border-red-900/40 bg-[#0D0D0D] p-8"
    >
      <img src={LOGO_URL} alt="Linea Cremisi" className="h-12 w-12 rounded-full border border-red-900/50 object-cover" />
      <p className="font-mono-tac mt-6 text-[10px] uppercase tracking-[0.35em] text-red-600">
        Area riservata
      </p>
      <h1 className="font-display mt-2 text-4xl uppercase text-neutral-100">Accesso band</h1>
      <label className="font-mono-tac mt-8 block text-[10px] uppercase tracking-[0.3em] text-neutral-500">
        Email
      </label>
      <input
        data-testid="admin-email-input"
        type="email"
        autoComplete="username"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="mt-2 h-12 w-full border border-neutral-800 bg-black/40 px-4 text-neutral-100 focus:border-red-700 focus:outline-none"
      />
      <label className="font-mono-tac mt-5 block text-[10px] uppercase tracking-[0.3em] text-neutral-500">
        Password
      </label>
      <input
        data-testid="admin-password-input"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className="mt-2 h-12 w-full border border-neutral-800 bg-black/40 px-4 text-neutral-100 focus:border-red-700 focus:outline-none"
      />
      {error && (
        <p data-testid="admin-login-error" role="alert" className="mt-4 text-sm text-red-500">
          {error}
        </p>
      )}
      <button
        data-testid="admin-login-submit"
        type="submit"
        disabled={loading}
        className="mt-7 h-12 w-full bg-[#C8102E] font-mono-tac text-sm uppercase tracking-[0.2em] text-white transition-colors duration-200 hover:bg-[#FF2A4B] disabled:opacity-60"
      >
        {loading ? "Accesso…" : "Entra"}
      </button>
    </form>
  );
}

function SubscribersPanel({ headers, onLogout }) {
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/subscribers`, { headers: headers() });
      setRows(data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast("Impossibile caricare gli iscritti");
    }
  }, [headers, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (row) => {
    if (!window.confirm(`Rimuovere ${row.email} dalla lista?`)) return;
    try {
      await axios.delete(`${API}/api/admin/subscribers/${row.id}`, { headers: headers() });
      setRows((r) => r.filter((x) => x.id !== row.id));
      toast("Iscritto rimosso", { description: row.email });
    } catch {
      toast("Errore durante la rimozione");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/subscribers/export.csv`, {
        headers: headers(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "linea-cremisi-iscritti.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast("Export non riuscito");
    }
  };

  return (
    <div>
      <div className="flex justify-end">
        <button
          data-testid="admin-export-csv"
          onClick={exportCsv}
          className="inline-flex items-center gap-2 bg-[#C8102E] px-5 py-2.5 font-mono-tac text-xs uppercase tracking-[0.2em] text-white transition-colors duration-200 hover:bg-[#FF2A4B]"
        >
          <Download className="h-4 w-4" /> Esporta CSV
        </button>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div className="border border-neutral-800 bg-[#0D0D0D] p-5">
          <Users className="h-4 w-4 text-red-600" />
          <p data-testid="admin-stat-total" className="font-display mt-3 text-4xl text-neutral-100">
            {rows ? rows.length : "…"}
          </p>
          <p className="font-mono-tac text-[10px] uppercase tracking-[0.3em] text-neutral-500">Iscritti</p>
        </div>
        <div className="border border-neutral-800 bg-[#0D0D0D] p-5">
          <Mail className="h-4 w-4 text-red-600" />
          <p data-testid="admin-stat-welcomed" className="font-display mt-3 text-4xl text-neutral-100">
            {rows ? rows.filter((r) => r.welcome_sent_at).length : "…"}
          </p>
          <p className="font-mono-tac text-[10px] uppercase tracking-[0.3em] text-neutral-500">Benvenuti inviati</p>
        </div>
        <div className="col-span-2 border border-red-900/40 bg-red-950/10 p-5 text-sm leading-relaxed text-neutral-400 sm:col-span-1">
          Per inviare una newsletter a tutti, esporta il CSV e importalo nel tuo
          strumento di invio (Mailchimp, Brevo…).
        </div>
      </div>

      <div data-testid="admin-subscribers-table" className="mt-6 overflow-x-auto border border-neutral-800 bg-[#0D0D0D]">
        <table className="w-full text-left text-sm">
          <thead className="font-mono-tac text-[10px] uppercase tracking-[0.25em] text-neutral-500">
            <tr className="border-b border-neutral-800">
              <th className="px-5 py-3">Email</th>
              <th className="px-5 py-3">Iscritto il</th>
              <th className="px-5 py-3">Benvenuto</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody>
            {rows && rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-5 py-10 text-center text-neutral-500">
                  Nessun iscritto ancora.
                </td>
              </tr>
            )}
            {rows?.map((r) => (
              <tr key={r.id} data-testid="admin-subscriber-row" className="border-b border-neutral-800/60 last:border-b-0">
                <td className="px-5 py-3 text-neutral-100">{r.email}</td>
                <td className="px-5 py-3 text-neutral-400">{fmtDate(r.subscribed_at)}</td>
                <td className="px-5 py-3">
                  <span className={`font-mono-tac text-[10px] uppercase tracking-[0.2em] ${r.welcome_sent_at ? "text-red-500" : "text-neutral-600"}`}>
                    {r.welcome_sent_at ? "Inviato" : "—"}
                  </span>
                </td>
                <td className="px-5 py-3 text-right">
                  <button
                    data-testid="admin-subscriber-delete"
                    onClick={() => remove(r)}
                    aria-label={`Rimuovi ${r.email}`}
                    className="text-neutral-600 transition-colors duration-200 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminShell({ token, user, onLogout }) {
  const [tab, setTab] = useState("bookings");
  const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);
  return (
    <div className="w-full max-w-5xl self-start">
      <header className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
        <div>
          <p data-testid="admin-user-email" className="font-mono-tac text-[10px] uppercase tracking-[0.35em] text-red-600">
            Area riservata — {user?.email}
          </p>
          <h1 className="font-display mt-2 text-5xl uppercase leading-none text-neutral-100 sm:text-6xl">
            Pannello <span className="text-[#C8102E]">band</span>
          </h1>
        </div>
        <button
          data-testid="admin-logout"
          onClick={onLogout}
          className="inline-flex items-center gap-2 border border-neutral-800 px-4 py-3 font-mono-tac text-xs uppercase tracking-[0.2em] text-neutral-400 transition-colors duration-200 hover:border-red-700 hover:text-red-500"
        >
          <LogOut className="h-4 w-4" /> Esci
        </button>
      </header>

      <nav className="mt-8 flex flex-wrap gap-2 border-b border-neutral-800">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-3 font-mono-tac text-xs uppercase tracking-[0.2em] transition-colors duration-200 ${
              tab === t.id ? "border-[#C8102E] text-neutral-100" : "border-transparent text-neutral-500 hover:text-red-500"
            }`}
          >
            <t.Icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </nav>

      <div className="mt-8">
        {tab === "bookings" && <BookingsAdmin headers={headers} onUnauthorized={onLogout} />}
        {tab === "concerts" && <ConcertsAdmin headers={headers} onUnauthorized={onLogout} />}
        {tab === "gallery" && <GalleryAdmin headers={headers} onUnauthorized={onLogout} />}
        {tab === "members" && <MembersAdmin headers={headers} onUnauthorized={onLogout} />}
        {tab === "orders" && <OrdersAdmin headers={headers} onUnauthorized={onLogout} />}
        {tab === "shop" && <ShopAdmin headers={headers} onUnauthorized={onLogout} />}
        {tab === "subscribers" && <SubscribersPanel headers={headers} onLogout={onLogout} />}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(Boolean(token));

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setUser(data))
      .catch(logout)
      .finally(() => setChecking(false));
  }, [token, logout]);

  const login = (newToken, newUser) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    setUser(newUser);
    setChecking(false);
  };

  return (
    <div data-testid="admin-page" className="flex min-h-screen items-center justify-center bg-[#070707] px-5 py-16 text-neutral-200 sm:px-8">
      <div className="grain-overlay" aria-hidden="true" />
      <Link
        data-testid="admin-back-to-site"
        to="/"
        className="fixed left-5 top-5 z-10 font-mono-tac text-[10px] uppercase tracking-[0.3em] text-neutral-500 transition-colors duration-200 hover:text-red-500 sm:left-8"
      >
        ← Torna al sito
      </Link>
      {checking ? (
        <p className="font-mono-tac text-xs uppercase tracking-[0.3em] text-neutral-500">Verifica accesso…</p>
      ) : token && user ? (
        <AdminShell token={token} user={user} onLogout={logout} />
      ) : (
        <LoginForm onLogin={login} />
      )}
    </div>
  );
}
